import React from 'react';
import { TaskItem, TaskSection } from '../types/dashboardTypes';
import { useDroppable } from '@dnd-kit/core';
import { cn } from '../../../lib/utils';
import DashboardTaskColumn from '../DashboardTaskColumn';
import DraggableDashboardTaskCard from './DraggableDashboardTaskCard';
import { LucideIcon } from 'lucide-react';
import { Types } from './taskTypes';

interface DroppableDashboardColumnProps {
  title: string;
  icon: LucideIcon;
  iconBgColor: string;
  iconColor: string;
  tasks: TaskItem[];
  sectionType: TaskSection;
  projects: Array<{ id: string; name: string }>;
  isMobile: boolean;
  onEditTask: (task: TaskItem) => void;
  onUpdateTask: (taskId: string, updates: Partial<TaskItem>) => Promise<void>;
  onDeleteTask: (taskId: string) => Promise<void>;
  onNavigateToTask: (task: TaskItem) => void;
  activeTaskId: string | null;
}

/**
 * Droppable dashboard column that wraps the standard DashboardTaskColumn
 * and renders draggable tasks inside it
 */
const DroppableDashboardColumn: React.FC<DroppableDashboardColumnProps> = ({
  title,
  icon,
  iconBgColor,
  iconColor,
  tasks,
  sectionType,
  projects,
  isMobile,
  onEditTask,
  onUpdateTask,
  onDeleteTask,
  onNavigateToTask,
}) => {
  // Determine if this is the Overdue section (which should not accept drops)
  const isOverdueSection = sectionType === 'overdue';

  // Use the section type directly as the ID and status
  // The key difference from task topic: we use sectionType instead of column.status
  const { setNodeRef } = useDroppable({
    id: sectionType,
    data: {
      type: Types.COLUMN,
      section: sectionType, // Use section instead of status
      id: sectionType, // Keep ID the same to match task topic pattern
    },
    // Only disable dropping for the Overdue column
    disabled: isOverdueSection,
  });

  // Render the column content using DashboardTaskColumn as a wrapper
  return (
    <div ref={setNodeRef} className={cn('flex flex-col h-full relative')}>
      <DashboardTaskColumn
        title={title}
        icon={icon}
        iconBgColor={iconBgColor}
        iconColor={iconColor}
        tasks={tasks}
        sectionType={sectionType}
        projects={projects}
        isMobile={isMobile}
        onEditTask={onEditTask}
        onUpdateTask={onUpdateTask}
        onDeleteTask={onDeleteTask}
        onNavigateToTask={onNavigateToTask}
      >
        {tasks.length > 0 ? (
          <div
            className={`space-y-3 ${
              isMobile
                ? 'overflow-visible pb-4' // Reduced padding for mobile
                : 'overflow-y-auto pr-1 h-full max-h-[calc(100vh-160px)]'
            } scroll-container rounded-lg`}
          >
            {/* We're not using SortableContext because we don't need sorting in the dashboard */}
            {tasks.map((task, index) => (
              <DraggableDashboardTaskCard
                key={task.id}
                task={task}
                sectionType={sectionType}
                projects={projects}
                index={index}
                onEditTask={onEditTask}
                onUpdateTask={onUpdateTask}
                onDeleteTask={onDeleteTask}
                onNavigateToTask={onNavigateToTask}
              />
            ))}
          </div>
        ) : null}
      </DashboardTaskColumn>
    </div>
  );
};

export default DroppableDashboardColumn;
